import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';

const RealTimeAlertPanel = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const panelRef = useRef(null);
  const subscriptionsRef = useRef([]);

  useEffect(() => {
    if (isOpen) {
      setupRealtimeSubscriptions();
    }
    return () => {
      cleanupSubscriptions();
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef?.current && !panelRef?.current?.contains(e?.target)) {
        onClose?.();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const cleanupSubscriptions = () => {
    subscriptionsRef?.current?.forEach(sub => sub?.unsubscribe?.());
    subscriptionsRef.current = [];
  };

  const addAlert = (alert) => {
    setAlerts(prev => {
      const exists = prev?.some(a => a?.id === alert?.id);
      if (exists) return prev;
      return [alert, ...prev]?.slice(0, 50);
    });
    setUnreadCount(prev => prev + 1);
  };

  const setupRealtimeSubscriptions = () => {
    cleanupSubscriptions();

    // Subscribe to ticket changes
    const ticketSub = supabase
      ?.channel('realtime-tickets')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'tickets'
      }, (payload) => {
        addAlert({
          id: `ticket-insert-${payload?.new?.id}-${Date.now()}`,
          type: 'ticket',
          event: 'created',
          title: 'New Ticket Created',
          message: `Ticket ${payload?.new?.ticket_number || 'New'} has been created`,
          timestamp: new Date()?.toISOString(),
          icon: 'ClipboardList',
          iconColor: 'var(--color-primary)',
          actionPath: '/ticket-management',
          read: false
        });
      })
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'tickets'
      }, (payload) => {
        addAlert({
          id: `ticket-update-${payload?.new?.id}-${Date.now()}`,
          type: 'ticket',
          event: 'updated',
          title: 'Ticket Updated',
          message: `Ticket ${payload?.new?.ticket_number || ''} has been updated`,
          timestamp: new Date()?.toISOString(),
          icon: 'RefreshCw',
          iconColor: 'var(--color-warning)',
          actionPath: '/ticket-management',
          read: false
        });
      })
      ?.subscribe();

    // Subscribe to ticket_technicians (assignments)
    const assignmentSub = supabase
      ?.channel('realtime-assignments')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'ticket_technicians'
      }, (payload) => {
        addAlert({
          id: `assignment-${payload?.new?.id}-${Date.now()}`,
          type: 'assignment',
          event: 'assigned',
          title: 'New Technician Assignment',
          message: 'A technician has been assigned to a ticket',
          timestamp: new Date()?.toISOString(),
          icon: 'UserCheck',
          iconColor: 'var(--color-secondary)',
          actionPath: '/technician-assignment',
          read: false
        });
      })
      ?.subscribe();

    // Subscribe to quote changes
    const quoteSub = supabase
      ?.channel('realtime-quotes')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'quotes'
      }, (payload) => {
        addAlert({
          id: `quote-insert-${payload?.new?.id}-${Date.now()}`,
          type: 'quote',
          event: 'created',
          title: 'New Quote Created',
          message: `Quote ${payload?.new?.quote_number || 'New'} has been created`,
          timestamp: new Date()?.toISOString(),
          icon: 'FileText',
          iconColor: 'var(--color-accent)',
          actionPath: '/quote-management',
          read: false
        });
      })
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'quotes'
      }, (payload) => {
        addAlert({
          id: `quote-update-${payload?.new?.id}-${Date.now()}`,
          type: 'quote',
          event: 'updated',
          title: 'Quote Status Changed',
          message: `Quote ${payload?.new?.quote_number || ''} has been updated`,
          timestamp: new Date()?.toISOString(),
          icon: 'FileCheck',
          iconColor: 'var(--color-success)',
          actionPath: '/quote-management',
          read: false
        });
      })
      ?.subscribe();

    // Subscribe to support requests
    const requestSub = supabase
      ?.channel('realtime-requests')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'support_requests'
      }, (payload) => {
        addAlert({
          id: `request-${payload?.new?.id}-${Date.now()}`,
          type: 'request',
          event: 'created',
          title: 'New Support Request',
          message: 'A new support request has been submitted',
          timestamp: new Date()?.toISOString(),
          icon: 'Headphones',
          iconColor: 'var(--color-error)',
          actionPath: '/support-request-management',
          read: false
        });
      })
      ?.subscribe();

    subscriptionsRef.current = [ticketSub, assignmentSub, quoteSub, requestSub];
  };

  const markAllRead = () => {
    setAlerts(prev => prev?.map(a => ({ ...a, read: true })));
    setUnreadCount(0);
  };

  const markRead = (alertId) => {
    setAlerts(prev => prev?.map(a => a?.id === alertId ? { ...a, read: true } : a));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const clearAll = () => {
    setAlerts([]);
    setUnreadCount(0);
  };

  const handleAlertClick = (alert) => {
    markRead(alert?.id);
    navigate(alert?.actionPath);
    onClose?.();
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d?.toLocaleDateString();
  };

  const getTypeLabel = (type) => {
    const labels = { ticket: 'Ticket', assignment: 'Assignment', quote: 'Quote', request: 'Request' };
    return labels?.[type] || type;
  };

  const getTypeBadgeClass = (type) => {
    const classes = {
      ticket: 'bg-primary/10 text-primary',
      assignment: 'bg-secondary/10 text-secondary',
      quote: 'bg-accent/10 text-accent',
      request: 'bg-error/10 text-error'
    };
    return classes?.[type] || 'bg-muted text-muted-foreground';
  };

  const filteredAlerts = filter === 'all' ? alerts : alerts?.filter(a => a?.type === filter);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[2000] flex items-start justify-end pt-16 pr-4">
      <div
        ref={panelRef}
        className="w-full max-w-sm bg-card rounded-xl shadow-elevation-5 border border-border flex flex-col max-h-[calc(100vh-80px)] animate-in slide-in-from-top-2"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Icon name="Bell" size={20} color="var(--color-primary)" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-error text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <h3 className="text-base font-heading font-semibold text-foreground">Live Alerts</h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              Live
            </span>
          </div>
          <div className="flex items-center gap-1">
            {alerts?.length > 0 && (
              <>
                <button
                  onClick={markAllRead}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors text-xs text-muted-foreground hover:text-foreground"
                  title="Mark all read"
                >
                  <Icon name="CheckCheck" size={16} color="currentColor" />
                </button>
                <button
                  onClick={clearAll}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors text-xs text-muted-foreground hover:text-foreground"
                  title="Clear all"
                >
                  <Icon name="Trash2" size={16} color="currentColor" />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="X" size={16} color="var(--color-foreground)" />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-1 px-4 py-2 border-b border-border flex-shrink-0 overflow-x-auto">
          {['all', 'ticket', 'assignment', 'quote', 'request']?.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {f === 'all' ? 'All' : getTypeLabel(f)}
              {f === 'all' && unreadCount > 0 && (
                <span className="ml-1 px-1 py-0.5 bg-error text-white text-xs rounded-full">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* Alerts List */}
        <div className="flex-1 overflow-y-auto">
          {filteredAlerts?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                <Icon name="BellOff" size={24} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">No alerts yet</p>
              <p className="text-xs text-muted-foreground">Real-time alerts will appear here when tickets, assignments, or quotes change.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredAlerts?.map(alert => (
                <div
                  key={alert?.id}
                  onClick={() => handleAlertClick(alert)}
                  className={`flex items-start gap-3 p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                    !alert?.read ? 'bg-primary/3' : ''
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${alert?.iconColor}15` }}
                  >
                    <Icon name={alert?.icon} size={16} color={alert?.iconColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className={`text-sm font-medium leading-tight ${
                        !alert?.read ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {alert?.title}
                      </p>
                      {!alert?.read && (
                        <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-1.5">{alert?.message}</p>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getTypeBadgeClass(alert?.type)}`}>
                        {getTypeLabel(alert?.type)}
                      </span>
                      <span className="text-xs text-muted-foreground">{formatTime(alert?.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-border flex-shrink-0">
          <p className="text-xs text-center text-muted-foreground">
            Subscribed to tickets, assignments & quotes
          </p>
        </div>
      </div>
    </div>
  );
};

export { RealTimeAlertPanel };
export default RealTimeAlertPanel;
